import {
  Stack,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { MouseEvent } from 'react';
import { SearchBar } from '../Shell/SearchBar';
import {
  AssignedIndividualVolunteer,
  CombinedFamilyInfo,
  Person,
} from '../GeneratedClient';
import { CountyFilter } from '../Generic/CountyFilter';
import {
  Add as AddIcon,
  UnfoldLess as UnfoldLessIcon,
  UnfoldMore as UnfoldMoreIcon,
} from '@mui/icons-material';
import { AssignmentRoleFilters } from '../FunctionAssignments/AssignmentRoleFilters';
import { AssignmentFilterSelectionsByRole } from '../FunctionAssignments/assignmentRoleColumns';

export type ReferralStatusFilter = 'ALL' | 'OPEN' | 'ACCEPTED' | 'CLOSED';

interface ReferralsFiltersProps {
  filterText: string;
  setFilterText: (value: string) => void;

  expandedView: boolean;
  setExpandedView: (value: boolean) => void;

  canAddNewReferral: boolean;
  onAddNewReferral: () => void;

  statusFilter: ReferralStatusFilter;
  setStatusFilter: (value: ReferralStatusFilter) => void;

  countyFilter: (string | null)[];
  setCountyFilter: (value: (string | null)[]) => void;

  familiesForCountyFilter: CombinedFamilyInfo[];

  assignmentRoles: string[];
  assignmentsForAssignmentFilter: AssignedIndividualVolunteer[];
  assignmentFilters: AssignmentFilterSelectionsByRole;
  setAssignmentFilter: (
    assignmentRole: string,
    selectedValues: (string | null)[]
  ) => void;
  assignmentPersonLookup: (personId: string) => Person | undefined;
}

export function ReferralsFilters({
  filterText,
  setFilterText,
  expandedView,
  setExpandedView,
  canAddNewReferral,
  onAddNewReferral,
  statusFilter,
  setStatusFilter,
  countyFilter,
  setCountyFilter,
  familiesForCountyFilter,
  assignmentRoles,
  assignmentsForAssignmentFilter,
  assignmentFilters,
  setAssignmentFilter,
  assignmentPersonLookup,
}: ReferralsFiltersProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleExpandCollapse = (
    _event: MouseEvent<HTMLElement>,
    value: boolean | null
  ) => {
    if (value !== null) {
      setExpandedView(value);
    }
  };

  return (
    <Stack
      direction="row"
      sx={{ mt: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center', gap: 1 }}
    >
      {canAddNewReferral && (
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={onAddNewReferral}
        >
          Add new referral
        </Button>
      )}

      <ToggleButtonGroup
        value={statusFilter}
        exclusive
        onChange={(_, value) => value && setStatusFilter(value)}
        size={isMobile ? 'medium' : 'small'}
        sx={{ ml: 'auto' }}
      >
        <ToggleButton value="ALL">ALL</ToggleButton>
        <ToggleButton value="OPEN">OPEN</ToggleButton>
        <ToggleButton value="ACCEPTED">ACCEPTED</ToggleButton>
        <ToggleButton value="CLOSED">CLOSED</ToggleButton>
      </ToggleButtonGroup>

      <CountyFilter
        families={familiesForCountyFilter}
        value={countyFilter}
        onChange={setCountyFilter}
      />

      <AssignmentRoleFilters
        assignmentRoles={assignmentRoles}
        assignments={assignmentsForAssignmentFilter}
        selectedValuesByRole={assignmentFilters}
        onChange={setAssignmentFilter}
        personLookup={assignmentPersonLookup}
      />

      <SearchBar value={filterText} onChange={setFilterText} />

      <ToggleButtonGroup
        value={expandedView}
        exclusive
        onChange={handleExpandCollapse}
        size={isMobile ? 'medium' : 'small'}
      >
        <ToggleButton value={true}>
          <UnfoldMoreIcon />
        </ToggleButton>

        <ToggleButton value={false}>
          <UnfoldLessIcon />
        </ToggleButton>
      </ToggleButtonGroup>
    </Stack>
  );
}
